import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast } from './utils.js';

const MODULE_META = {
  listing: { icon: '📝', accent: 'amber', tag: 'core', shortTitle: 'Listing' },
  'image-suite': { icon: '🖼️', accent: 'purple', tag: 'media', shortTitle: '套图' },
  'video-suite': { icon: '🎬', accent: 'pink', tag: 'media', shortTitle: '视频' },
  'ad-optimization': { icon: '📈', accent: 'blue', tag: 'analysis', shortTitle: '广告' },
  'product-research': { icon: '🔭', accent: 'teal', tag: 'research', shortTitle: '选品' },
  'dify-assistant': { icon: '🐝', accent: 'yellow', tag: 'tools', shortTitle: '蜂蚁' },
  'ai-chat': { icon: '💬', accent: 'cyan', tag: 'tools', shortTitle: '对话' },
  'restock-advice': { icon: '⚖️', accent: 'orange', tag: 'planning', shortTitle: '备货' },
};

const MODULE_ORDER = [
  'listing',
  'image-suite',
  'video-suite',
  'ad-optimization',
  'product-research',
  'dify-assistant',
  'ai-chat',
  'restock-advice',
];

const state = {
  modules: [],
  workspaceModules: [],
  groups: [],
  groupVisibility: new Map(),
  loaded: false,
};

export function initModuleManagement() {
  registerViewLoader('admin-modules', () => loadModuleManagement());
  document.getElementById('module-visibility-group-select')?.addEventListener('change', () => {
    locateMatrixRow('module-visibility-group-matrix', 'groupId', getSelectedGroupId());
  });
  document.getElementById('module-visibility-save-group-btn')?.addEventListener('click', () => saveGroupVisibility());
}

function moduleSort(a, b) {
  const aIndex = MODULE_ORDER.indexOf(a.key);
  const bIndex = MODULE_ORDER.indexOf(b.key);
  if (aIndex !== -1 || bIndex !== -1) {
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  }
  const groupCompare = String(a.group || a.frontend?.group || '').localeCompare(String(b.group || b.frontend?.group || ''));
  return groupCompare || String(a.key || '').localeCompare(String(b.key || ''));
}

function normalizeAssignableModule(mod) {
  const workspace = state.workspaceModules.find((item) => item.key === mod.key) || {};
  return {
    ...workspace,
    ...mod,
    title: mod.title || workspace.title || mod.key,
    status: workspace.status || 'active',
    group: mod.group || workspace.frontend?.group || '',
    entry: mod.entry || workspace.frontend?.entry || '',
  };
}

function getModules() {
  return state.modules.map(normalizeAssignableModule).sort(moduleSort);
}

function getModuleMeta(moduleKey) {
  return MODULE_META[moduleKey] || { icon: '□', accent: 'blue', tag: 'module', shortTitle: moduleKey };
}

function setText(id, text) {
  const el = document.getElementById(id);
  if (el) el.textContent = text || '';
}

function createModuleElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = String(text);
  return element;
}

function replaceModuleChildren(element, ...children) {
  if (element) element.replaceChildren(...children);
  return element;
}

function createModuleEmptyState(message) {
  const state = createModuleElement('div', 'empty-state');
  state.append(createModuleElement('p', '', message));
  return state;
}

function createMatrixEmptyRow(colspan, message) {
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  cell.colSpan = colspan;
  cell.append(createModuleEmptyState(message));
  row.append(cell);
  return row;
}

function getSelectedGroupId() {
  return Number(document.getElementById('module-visibility-group-select')?.value || 0);
}

function selectedGroupMatrixValues(groupId) {
  const expectedGroupId = String(groupId);
  return Array.from(document.querySelectorAll('#module-visibility-group-matrix input[type="checkbox"]:checked'))
    .filter((input) => input.dataset.groupId === expectedGroupId)
    .map((input) => input.value);
}

function sameStringSet(left = [], right = []) {
  const a = new Set(left);
  const b = new Set(right);
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

function locateMatrixRow(tbodyId, datasetKey, idValue) {
  const tbody = document.getElementById(tbodyId);
  if (!tbody || !idValue) return;
  const expected = String(idValue);
  tbody.querySelectorAll('tr').forEach((row) => {
    row.classList.toggle('module-admin-row-focus', row.dataset[datasetKey] === expected);
  });
  const target = Array.from(tbody.querySelectorAll('tr')).find((row) => row.dataset[datasetKey] === expected);
  target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

function renderStats() {
  const modules = getModules();
  const onlineCount = modules.filter((mod) => String(mod.status || '') === 'active').length;
  const configuredGroups = [...state.groupVisibility.values()].filter((item) => item?.configured).length;
  setText('module-stat-total', String(modules.length));
  setText('module-stat-online', String(onlineCount));
  setText('module-stat-pending', String(Math.max(modules.length - onlineCount, 0)));
  setText('module-stat-configured', `${configuredGroups}/${state.groups.length || 0}`);
}

function renderModules() {
  const host = document.getElementById('module-management-grid');
  if (!host) return;
  const modules = getModules();
  if (!modules.length) {
    replaceModuleChildren(host, createModuleEmptyState('暂无模块'));
    return;
  }
  const cards = modules.map((mod) => {
    const meta = getModuleMeta(mod.key);
    const status = String(mod.status || '') === 'active' ? 'online' : 'pending';
    const statusLabel = status === 'online' ? '已上线' : '待上线';
    const card = createModuleElement('article', `module-admin-card accent-${meta.accent}`);
    card.dataset.moduleKey = String(mod.key || '');
    const header = createModuleElement('div', 'module-admin-card-header');
    const icon = createModuleElement('span', 'module-admin-icon', meta.icon);
    icon.setAttribute('aria-hidden', 'true');
    header.append(icon, createModuleElement('span', `module-admin-status ${status}`, statusLabel));
    const cardMeta = createModuleElement('div', 'module-admin-meta');
    cardMeta.append(createModuleElement('span', 'module-admin-tag', meta.tag || mod.group || 'module'));
    card.append(
      header,
      createModuleElement('div', 'module-admin-title', mod.title || mod.key),
      createModuleElement('div', 'module-admin-key', mod.key),
      cardMeta,
      createModuleElement('div', 'module-admin-path', mod.entry || ''),
    );
    return card;
  });
  replaceModuleChildren(host, ...cards);
}

function renderVisibilitySelectors() {
  const groupSelect = document.getElementById('module-visibility-group-select');
  if (groupSelect) {
    const previous = groupSelect.value;
    const options = state.groups.map((group) => {
      const option = createModuleElement('option', '', group.name);
      option.value = String(group.id);
      return option;
    });
    replaceModuleChildren(groupSelect, ...options);
    if (previous && state.groups.some((group) => String(group.id) === previous)) groupSelect.value = previous;
  }

}

function renderMatrixHead(headId, firstColumnLabel = '分组') {
  const head = document.getElementById(headId);
  if (!head) return;
  const modules = getModules();
  const row = document.createElement('tr');
  row.append(createModuleElement('th', '', firstColumnLabel));
  modules.forEach((mod) => {
    const meta = getModuleMeta(mod.key);
    row.append(createModuleElement('th', '', `${meta.icon} ${meta.shortTitle || mod.title || mod.key}`));
  });
  replaceModuleChildren(head, row);
}

function createVisibilityCheckbox(moduleKey, datasetKey, id, checked) {
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.className = 'module-admin-check';
  input.value = String(moduleKey || '');
  input.dataset[datasetKey] = String(id);
  input.checked = checked;
  return input;
}

function renderGroupMatrix() {
  renderMatrixHead('module-visibility-group-head');
  const body = document.getElementById('module-visibility-group-matrix');
  if (!body) return;
  const modules = getModules();
  const selectedGroupId = getSelectedGroupId() || Number(state.groups[0]?.id || 0);
  if (!state.groups.length || !modules.length) {
    replaceModuleChildren(body, createMatrixEmptyRow(modules.length + 1, '暂无可配置分组或模块'));
    return;
  }
  const rows = state.groups.map((group) => {
    const visibility = state.groupVisibility.get(Number(group.id));
    const enabled = new Set(visibility?.enabledModules || modules.map((mod) => mod.key));
    const row = document.createElement('tr');
    row.dataset.groupId = String(group.id);
    row.append(createModuleElement('td', '', group.name));
    modules.forEach((mod) => {
      const cell = document.createElement('td');
      cell.append(createVisibilityCheckbox(mod.key, 'groupId', group.id, enabled.has(mod.key)));
      row.append(cell);
    });
    return row;
  });
  replaceModuleChildren(body, ...rows);

  const selectedVisibility = state.groupVisibility.get(selectedGroupId);
  setText('module-visibility-group-status', selectedVisibility?.configured ? '当前分组已单独配置' : '当前分组继承默认全开');
  locateMatrixRow('module-visibility-group-matrix', 'groupId', selectedGroupId);
  renderStats();
}

async function loadVisibilityOverview() {
  const res = await authFetch('/api/admin/module-visibility');
  if (!res.ok) throw new Error('加载模块分配配置失败');
  const data = await res.json();
  state.modules = Array.isArray(data.modules) ? data.modules : [];
  state.groups = Array.isArray(data.groups) ? data.groups : [];
  renderVisibilitySelectors();
  await loadAllGroupVisibility();
}

async function loadAllGroupVisibility() {
  state.groupVisibility = new Map();
  await Promise.all(state.groups.map(async (group) => {
    const res = await authFetch(`/api/admin/module-visibility/groups/${encodeURIComponent(group.id)}`);
    if (!res.ok) throw new Error(`加载分组 ${group.name} 模块配置失败`);
    const data = await res.json();
    state.groupVisibility.set(Number(group.id), data);
  }));
  renderGroupMatrix();
}

async function saveGroupVisibility() {
  setText('module-visibility-group-status', '保存中...');
  try {
    const modules = getModules();
    const changedGroups = state.groups.filter((group) => {
      const next = selectedGroupMatrixValues(group.id);
      const previous = state.groupVisibility.get(Number(group.id))?.enabledModules || modules.map((mod) => mod.key);
      return !sameStringSet(next, previous);
    });
    await Promise.all(changedGroups.map(async (group) => {
      const res = await authFetch(`/api/admin/module-visibility/groups/${encodeURIComponent(group.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ enabledModules: selectedGroupMatrixValues(group.id) }),
      });
      if (!res.ok) throw new Error(`保存 ${group.name} 失败`);
      const data = await res.json();
      state.groupVisibility.set(Number(group.id), data);
    }));
    renderGroupMatrix();
    showToast(changedGroups.length ? '分组模块配置已保存' : '没有需要保存的分组改动', 'success');
  } catch (err) {
    console.error(err);
    setText('module-visibility-group-status', '保存失败');
    showToast('保存分组模块配置失败', 'error');
  }
}

export async function loadModuleManagement() {
  const grid = document.getElementById('module-management-grid');
  if (grid) replaceModuleChildren(grid, createModuleEmptyState('加载中...'));
  try {
    const [moduleRes] = await Promise.all([
      authFetch('/api/workspace/modules?status=active'),
      loadVisibilityOverview(),
    ]);
    if (!moduleRes.ok) throw new Error('加载失败');
    const data = await moduleRes.json();
    state.workspaceModules = Array.isArray(data.modules) ? data.modules : [];
    renderStats();
    renderModules();
    renderGroupMatrix();
    state.loaded = true;
  } catch (err) {
    console.error(err);
    if (grid) replaceModuleChildren(grid, createModuleEmptyState('加载失败'));
    showToast('加载模块清单失败', 'error');
  }
}
